/**
 * Timeline Clip Component (canvas-v2)
 *
 * Individual clip block with thumbnails that can be dragged to reorder.
 * Supports click to select, drag edges to trim.
 * V2 additions: 1:1 square thumbnails (width = clip height), speed badge when speed !== 1.0.
 *
 * Memoized for performance with large numbers of clips.
 */

import { useCallback, useState, useRef, memo } from 'react'
import type { TimelineClip as TimelineClipData } from './types'
import { getClipWidth, secondsToPixels, pixelsToSeconds, getClipPosition, findSnapPosition } from './timelineUtils'

interface TimelineClipProps {
  /** Clip data */
  clip: TimelineClipData

  /** All clips (for calculating position) */
  allClips: TimelineClipData[]

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Whether this clip is selected */
  isSelected: boolean

  /** Track height in pixels (used for 1:1 square thumbnails) */
  trackHeight: number

  /** Callback when clip is clicked */
  onSelect: (clipId: string) => void

  /** Callback when clip is reordered */
  onReorder: (clipId: string, newOrder: number) => void

  /** Callback when clip is trimmed */
  onTrim: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Callback for live trim preview (no history push) */
  onTrimPreview?: (clipId: string, trimStart: number, trimEnd: number) => void

  /** Callback when snap position changes during drag */
  onSnapPositionChange?: (position: number | null) => void

  /** Whether timeline is locked (no editing) */
  locked?: boolean
}

export const TimelineClipComponent = memo(function TimelineClipComponent({
  clip,
  allClips,
  zoomLevel,
  isSelected,
  trackHeight,
  onSelect,
  onReorder,
  onTrim,
  onTrimPreview,
  onSnapPositionChange,
  locked = false,
}: TimelineClipProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState<'move' | 'trim-start' | 'trim-end' | null>(null)
  const [dragOffset, setDragOffset] = useState(0)
  const dragStartXRef = useRef(0)
  const dragOffsetRef = useRef(0)
  const lastTrimRef = useRef<{ trimStart: number; trimEnd: number } | null>(null)
  const clipRef = useRef<HTMLDivElement>(null)

  const width = getClipWidth(clip, zoomLevel)
  const EDGE_RESIZE_WIDTH = 8 // pixels for edge resize handles

  // 1:1 square thumbnail size: width = available clip inner height
  const thumbSize = Math.max(trackHeight - 16, 32) // subtract top/bottom padding (8+8)

  // Thumbnail offset for trim-start: shift filmstrip left so trimmed frames are hidden
  const trimStartFraction = clip.originalDuration > 0 ? clip.trimStart / clip.originalDuration : 0
  const totalFilmstripWidth = thumbSize * (clip.thumbnails?.length || 0)
  const thumbnailOffset = -(trimStartFraction * totalFilmstripWidth)
  // +1 buffer ensures the filmstrip fills the clip all the way to the right edge
  // during trim-start drag, preventing blank space on the right.
  const neededThumbs = Math.ceil(width / thumbSize) + Math.ceil(Math.abs(thumbnailOffset) / thumbSize) + 1

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, type: 'move' | 'trim-start' | 'trim-end') => {
      if (locked) return

      e.preventDefault()
      e.stopPropagation()

      setIsDragging(true)
      setDragType(type)
      dragStartXRef.current = e.clientX
      dragOffsetRef.current = 0

      if (type === 'move') {
        onSelect(clip.id)
      }

      // Initialize lastTrimRef at drag start so we accumulate from the current values
      if (type === 'trim-start' || type === 'trim-end') {
        lastTrimRef.current = { trimStart: clip.trimStart, trimEnd: clip.trimEnd }
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - dragStartXRef.current

        if (type === 'move') {
          dragOffsetRef.current = deltaX
          setDragOffset(deltaX)

          // Calculate current position of clip
          const currentClipPosition = getClipPosition(clip, allClips, zoomLevel)
          const newPosition = currentClipPosition + deltaX

          // Check for snap position
          const otherClips = allClips.filter((c) => c.id !== clip.id)
          const snapPos = findSnapPosition(clip, otherClips, newPosition, zoomLevel)

          // Notify parent of snap position
          if (onSnapPositionChange) {
            onSnapPositionChange(snapPos)
          }

          // If snapping, adjust drag offset to snap position
          if (snapPos !== null) {
            const snappedOffset = snapPos - currentClipPosition
            dragOffsetRef.current = snappedOffset
            setDragOffset(snappedOffset)
          }
        } else if (type === 'trim-start') {
          // Trim start: dragging right increases trim
          const deltaSec = pixelsToSeconds(deltaX, zoomLevel)
          const currentTrimStart = lastTrimRef.current?.trimStart ?? clip.trimStart
          const currentTrimEnd = lastTrimRef.current?.trimEnd ?? clip.trimEnd
          const newTrimStart = Math.max(
            0,
            Math.min(currentTrimStart + deltaSec, clip.originalDuration - currentTrimEnd - 0.1)
          )
          lastTrimRef.current = { trimStart: newTrimStart, trimEnd: currentTrimEnd }
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, newTrimStart, currentTrimEnd)
          dragStartXRef.current = moveEvent.clientX
        } else if (type === 'trim-end') {
          // Trim end: dragging left increases trim
          const deltaSec = pixelsToSeconds(-deltaX, zoomLevel)
          const currentTrimStart = lastTrimRef.current?.trimStart ?? clip.trimStart
          const currentTrimEnd = lastTrimRef.current?.trimEnd ?? clip.trimEnd
          const newTrimEnd = Math.max(
            0,
            Math.min(currentTrimEnd + deltaSec, clip.originalDuration - currentTrimStart - 0.1)
          )
          lastTrimRef.current = { trimStart: currentTrimStart, trimEnd: newTrimEnd }
          const trimPreview = onTrimPreview || onTrim
          trimPreview(clip.id, currentTrimStart, newTrimEnd)
          dragStartXRef.current = moveEvent.clientX
        }
      }

      const handleMouseUp = () => {
        if (type === 'move' && Math.abs(dragOffsetRef.current) > 5) {
          // Calculate new order based on drag offset
          const newPosition = secondsToPixels(
            allClips
              .filter((c) => c.order < clip.order)
              .reduce((sum, c) => sum + c.effectiveDuration, 0),
            zoomLevel
          ) + dragOffsetRef.current

          // Find new order
          let cumulativePos = 0
          let newOrder = 0
          const otherClips = allClips.filter((c) => c.id !== clip.id).sort((a, b) => a.order - b.order)

          for (let i = 0; i < otherClips.length; i++) {
            if (newPosition < cumulativePos + secondsToPixels(otherClips[i].effectiveDuration / 2, zoomLevel)) {
              break
            }
            cumulativePos += secondsToPixels(otherClips[i].effectiveDuration, zoomLevel)
            newOrder++
          }

          if (newOrder !== clip.order) {
            onReorder(clip.id, newOrder)
          }
        }

        // Commit final trim value to history on mouseUp
        if ((type === 'trim-start' || type === 'trim-end') && lastTrimRef.current) {
          onTrim(clip.id, lastTrimRef.current.trimStart, lastTrimRef.current.trimEnd)
          lastTrimRef.current = null
        }

        setIsDragging(false)
        setDragType(null)
        setDragOffset(0)
        dragOffsetRef.current = 0

        // Clear snap indicator
        if (onSnapPositionChange) {
          onSnapPositionChange(null)
        }

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [locked, clip, allClips, zoomLevel, onSelect, onReorder, onTrim, onTrimPreview, onSnapPositionChange]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!locked && !isDragging) {
        e.stopPropagation()
        onSelect(clip.id)
      }
    },
    [locked, isDragging, clip.id, onSelect]
  )

  const handleEdgeMouseDown = useCallback(
    (e: React.MouseEvent, edge: 'start' | 'end') => {
      e.stopPropagation()
      handleMouseDown(e, edge === 'start' ? 'trim-start' : 'trim-end')
    },
    [handleMouseDown]
  )

  return (
    <div
      ref={clipRef}
      className={`
        absolute top-0 h-full
        bg-teal-700/60 border border-teal-500
        rounded
        overflow-hidden
        ${isSelected ? 'ring-2 ring-blue-400' : ''}
        ${isDragging && dragType === 'move' ? 'opacity-50 cursor-grabbing' : 'cursor-grab'}
        ${locked ? 'cursor-not-allowed' : ''}
        ${!isDragging ? 'transition-transform duration-200 ease-out' : ''}
      `}
      style={{
        width: `${width}px`,
        transform: isDragging && dragType === 'move' ? `translateX(${dragOffset}px)` : 'none',
      }}
      onMouseDown={(e) => !locked && handleMouseDown(e, 'move')}
      onClick={handleClick}
    >
      {/* Thumbnail filmstrip background -- 1:1 square thumbnails */}
      {clip.thumbnails && clip.thumbnails.length > 0 && (
        <div className="absolute inset-0 flex overflow-hidden pointer-events-none">
          <div className="flex flex-shrink-0" style={{ transform: `translateX(${thumbnailOffset}px)` }}>
            {clip.thumbnails.slice(0, neededThumbs).map((thumb, idx) => (
              <img
                key={idx}
                src={thumb}
                alt=""
                className="object-cover flex-shrink-0"
                style={{ width: `${thumbSize}px`, height: `${thumbSize}px` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Label + duration overlay on top of filmstrip */}
      <div className="relative h-full flex flex-col justify-end px-2 py-1 pointer-events-none z-10">
        <div className="flex items-center gap-2">
          <span className="text-foreground text-xs font-medium truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {clip.label}
          </span>
          <span className="text-foreground text-xs tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            {formatDuration(clip.effectiveDuration)}
          </span>
          {/* Speed badge */}
          {clip.speed !== 1.0 && (
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-500/80 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
              {clip.speed}x
            </span>
          )}
        </div>
      </div>

      {/* Left edge resize handle */}
      {!locked && (
        <div
          className="absolute left-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/50 transition-colors group"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'start')}
        >
          {/* Visual indicator on hover */}
          <div className="absolute inset-y-0 left-0 w-1 bg-blue-500/0 group-hover:bg-blue-500/70 transition-colors" />
        </div>
      )}

      {/* Right edge resize handle */}
      {!locked && (
        <div
          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize hover:bg-blue-400/50 transition-colors group"
          style={{ width: `${EDGE_RESIZE_WIDTH}px` }}
          onMouseDown={(e) => handleEdgeMouseDown(e, 'end')}
        >
          {/* Visual indicator on hover */}
          <div className="absolute inset-y-0 right-0 w-1 bg-blue-500/0 group-hover:bg-blue-500/70 transition-colors" />
        </div>
      )}
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
